import React, { useCallback, useRef } from 'react';
import ReactModal from 'react-modal';
import styled from '@emotion/styled';

import { AlphamartIntlProvider } from 'components/shared/AlphamartIntlProvider';
import { Details } from 'components/shared/Details';
import { LoadableContent } from 'components/shared/Loader';
import { NotificationsBell } from 'components/shared/NotificationsBell/NotificationsBell';
import { useAlphamartLocation, useClickAway, useMediaQuery } from 'shared/hooks';
import { clearNotificationDetails } from 'store/notificationDetails';
import { closeNotifications } from 'store/notificationsList';
import { useAppDispatch, useAppSelector } from 'store/shared/hooks';
import { layers, MEDIA_QUERY } from 'theme';
import { messages, TypedFormattedMessage } from './locale/messages';
import { NotificationDetails } from './NotificationDetails';
import { NotificationsFilters } from './NotificationsFilters';
import { NotificationsList } from './NotificationsList';

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  word-break: keep-all;
`;

export function Notifications(): React.ReactElement {
  const dispatch = useAppDispatch();
  const wrapperRef = useRef<HTMLDivElement>(null);

  const { isOpen } = useAppSelector(state => state.notificationsList);
  const isLoadingDetails = useAppSelector(state => state.notificationDetails.isPending);

  const close = useCallback(() => {
    dispatch(closeNotifications());
    return true;
  }, [dispatch]);

  const handleClickOutside = () => {
    if (isOpen) {
      dispatch(closeNotifications());
      dispatch(clearNotificationDetails());
    }
  };

  useClickAway(wrapperRef, handleClickOutside, ['click']);

  const location = useAlphamartLocation();

  const isMd = useMediaQuery(MEDIA_QUERY.MD);
  const isMaxXl = useMediaQuery(MEDIA_QUERY.MAX_XL);

  const noLabelLocations = ['/geolocation'];
  const compact =
    isMd && (isMaxXl || noLabelLocations.some(path => location.pathname.includes(path)));

  const notificationModalStyle = {
    overlay: {
      backgroundColor: 'transparent',
      zIndex: layers.notificationModal,
      [MEDIA_QUERY.MAX_MD]: {
        top: 64,
      },
      left: compact ? 97 : 230,
    },
    content: {
      backgroundColor: 'transparent',
      border: 'none',
      top: 0,
      right: 0,
      bottom: 0,
      left: compact ? 97 : 230,
    },
  };

  return (
    <AlphamartIntlProvider messages={messages}>
      <ReactModal
        isOpen={isOpen}
        style={notificationModalStyle}
        parentSelector={() => document.querySelector('#root') as HTMLElement}
      >
        <Details
          ref={wrapperRef}
          title={
            <Header>
              <NotificationsBell />
              <TypedFormattedMessage id="Notifications.Header" />
            </Header>
          }
          open
          onClose={close}
          options={{
            direction: isMd ? 'left' : 'right',
            width: '300px',
            offset: compact ? '97px' : '230px',
            header: { padding: 'M' },
          }}
        >
          <NotificationsFilters />
          <NotificationsList />
          <LoadableContent loading={isLoadingDetails}>
            <NotificationDetails />
          </LoadableContent>
        </Details>
      </ReactModal>
    </AlphamartIntlProvider>
  );
}
